import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';

import Img from 'retired/Img';
import CheckIcon from 'assets/icons/check-circle.svg';
import FileUpload from 'retired/FileUpload';

const styles = (theme) => ({
  grid: { padding: 0 },
  tile: {
    display: 'inline-block',
    position: 'relative',
    overflow: 'hidden',
    marginRight: 1,
    width: 'calc(25% - 1px)',
    paddingBottom: '14.0625%',
    [theme.breakpoints.only('md')]: {
      width: 'calc(33% - 1px)',
      paddingBottom: '18.5625%',
    },
    [theme.breakpoints.only('sm')]: {
      width: 'calc(50% - 1px)',
      paddingBottom: '28.125%',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingBottom: '56.25%',
    },
  },
  link: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    '&:focus': {
      outline: 'none',
      '&:after': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#000',
        opacity: 0.2,
        zIndex: 2,
      },
    },
    '&:hover': { cursor: 'pointer' },
  },
  selected: {
    '&:after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: '#000',
      opacity: 0.2,
      zIndex: 2,
    },
  },
  circle: {
    position: 'absolute',
    top: 5,
    right: 5,
    zIndex: 3,
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});

const PictureGrid = withStyles(styles)(
  ({ pictures, classes, fileUploadedAction, fileUploadName, formError }) => (
    <ul className={classes.grid}>
      {pictures.map((picture) => (
        <li key={picture.resource} className={classes.tile}>
          <div
            role="button"
            tabIndex="0"
            onClick={picture.onClick}
            onKeyDown={picture.onClick}
            className={[classes.link, picture.selected && classes.selected].join(' ')}
          >
            <img
              className={classes.image}
              src={picture.resource.replace(
                /https:\/\/(files|images).(.*\.?)(godeed.today|joindeed.org)\//g,
                'https://$1.$2$3/c960x540/'
              )}
              alt=""
            />
            {picture.selected && <Img src={CheckIcon} alt="" className={classes.circle} />}
          </div>
        </li>
      ))}
      <li className={classes.tile}>
        <FileUpload
          onFinish={fileUploadedAction}
          fixed
          name={fileUploadName}
          formError={formError}
        />
      </li>
    </ul>
  )
);
PictureGrid.propTypes = {
  cellHeight: PropTypes.number,
  cols: PropTypes.number,
  pictures: PropTypes.array,
  fileUploadedAction: PropTypes.func,
  fileUploadName: PropTypes.string,
};

const FormPictureSelector = ({
  fields,
  options,
  labelText,
  selectMultiple,
  selectedCategories,
  meta,
  pictures,
  fileUploadedAction,
  uploadedFile,
}) => {
  const selectedPictures = fields.getAll();
  let selectedPicturePartOfOptions = false;
  const onClick = (resource, selected) => (e) => {
    if (e.keyCode && e.keyCode !== 32 && e.keyCode !== 13) {
      return;
    }
    e.preventDefault();
    if (selected) {
      fields.remove(selectedPictures.indexOf(resource));
    } else {
      if (!selectMultiple) {
        fields.removeAll();
      }
      fields.push(resource);
    }
  };
  const selectedCategoriesArray = (selectedCategories && selectedCategories.toArray()) || [];
  const filteredOptions = options.reduce((accumulator, option) => {
    const included =
      _.intersection(option.get('categories').toArray(), selectedCategoriesArray).length > 0;
    if (!included) {
      return accumulator;
    }
    const selected = selectedPictures && selectedPictures.includes(option.resource) > 0;
    if (pictures && pictures.get(0) === option.resource) {
      selectedPicturePartOfOptions = true;
    }
    accumulator.push({
      ...option.toJS(),
      onClick: onClick(option.resource, selected),
      selected,
    });
    return accumulator;
  }, []);
  if (!selectedPicturePartOfOptions && pictures && pictures.size > 0) {
    const selected = selectedPictures && selectedPictures.get(0) === pictures.get(0);
    filteredOptions.unshift({
      resource: pictures.get(0),
      selected,
      onClick: onClick(pictures.get(0), selected),
    });
  }
  if (uploadedFile) {
    const selected = selectedPictures && selectedPictures.get(0) === uploadedFile;
    if (!selected) {
      filteredOptions.unshift({
        resource: uploadedFile,
        selected,
        onClick: onClick(uploadedFile, selected),
      });
    }
  }
  return (
    <FormControl style={{ display: 'block' }}>
      <InputLabel shrink>{labelText}</InputLabel>
      <br />
      <PictureGrid
        pictures={filteredOptions}
        fileUploadedAction={fileUploadedAction}
        fileUploadName={fields.name}
        formError={(meta.dirty || meta.submitFailed) && meta.error}
      />
      {(meta.dirty || meta.submitFailed) && meta.error && (
        <FormLabel error style={{ fontSize: 12, marginTop: 5 }}>
          {meta.error}
        </FormLabel>
      )}
    </FormControl>
  );
};
FormPictureSelector.propTypes = {
  options: PropTypes.object.isRequired,
  pictures: PropTypes.object,
  fields: PropTypes.object.isRequired,
  meta: PropTypes.object,
  selectMultiple: PropTypes.bool,
  labelText: PropTypes.string,
  selectedCategories: PropTypes.object,
  fileUploadedAction: PropTypes.func,
  uploadedFile: PropTypes.string,
};
export default FormPictureSelector;
